import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Select,
  Snackbar,
  TextField,
  Typography,
  InputAdornment,
} from "@mui/material";
import {
  listUiComponents,
  createUiComponent,
  updateUiComponent,
  getComponentRoles,
  replaceComponentRoles,
  patchComponentRoles,
} from "../../api/uiPermissions";
import { getAllRoles } from "../../api/roles";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";

export default function UiPermissionsAdmin() {
  const [components, setComponents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [newKey, setNewKey] = useState("");
  const [newGroup, setNewGroup] = useState("");
  const [newDesc, setNewDesc] = useState("");

  const [selectedId, setSelectedId] = useState(null);
  const [selectedActive, setSelectedActive] = useState(true);
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [allRoles, setAllRoles] = useState([]);
  const [search, setSearch] = useState("");
  console.log(components);

  // filter data
  const filteredData = components.filter((item) =>
    item.componentKey.toLowerCase().includes(search.toLowerCase())
  );
  const canCreate = useMemo(() => newKey.trim().length > 0, [newKey]);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const data = await listUiComponents();
      setComponents(Array.isArray(data) ? data : [data]);
    } catch (e) {
      setError("Failed to load components");
    } finally {
      setLoading(false);
    }
  };

  const loadRoles = async () => {
    try {
      const data = await getAllRoles();
      const arr = Array.isArray(data) ? data : [data];
      setAllRoles(arr.map((r) => r?.name).filter(Boolean));
    } catch {}
  };

  useEffect(() => {
    load();
    loadRoles();
  }, []);

  const handleCreate = async () => {
    if (!canCreate) return;
    setLoading(true);
    setError("");
    try {
      await createUiComponent({
        key: newKey.trim(),
        group: newGroup || undefined,
        description: newDesc || undefined,
      });
      setSuccess("Component created");
      setNewKey("");
      setNewGroup("");
      setNewDesc("");
      await load();
    } catch (e) {
      setError("Failed to create component");
    } finally {
      setLoading(false);
    }
  };

  const handleSelect = async (comp) => {
    setSelectedId(comp?.id);
    setSelectedActive(Boolean(comp?.active));
    try {
      const roles = await getComponentRoles(comp?.id);
      const arr = Array.isArray(roles) ? roles : [];
      setSelectedRoles(arr);
    } catch (e) {
      setSelectedRoles([]);
    }
  };

  const handleToggleActive = async () => {
    if (!selectedId) return;
    setLoading(true);
    try {
      await updateUiComponent(selectedId, { active: !selectedActive });
      setSelectedActive(!selectedActive);
      setSuccess("Component updated");
      await load();
    } catch (e) {
      setError("Failed to update component");
    } finally {
      setLoading(false);
    }
  };
  const handleReplaceComponent = async () => {
    if (!selectedId) return;
    setLoading(true);
    try {
      await updateUiComponent(selectedId, { allowedRoles: selectedId });

      setSuccess("Roles saved");
    } catch (e) {
      setError("Failed to save roles");
    } finally {
      setLoading(false);
    }
  };

  const handleReplaceRoles = async () => {
    if (!selectedId) return;
    setLoading(true);
    try {
      await replaceComponentRoles(selectedId, { allowedRoles: selectedRoles });
      setSuccess("Roles saved");
    } catch (e) {
      setError("Failed to save roles");
    } finally {
      setLoading(false);
    }
  };

  const handleAddRole = async (role) => {
    if (!selectedId || !role) return;
    setLoading(true);
    try {
      await patchComponentRoles(selectedId, { add: [role] });
      setSelectedRoles((prev) => Array.from(new Set([...(prev || []), role])));
      setSuccess("Role added");
    } catch (e) {
      setError("Failed to add role");
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveRole = async (role) => {
    if (!selectedId || !role) return;
    setLoading(true);
    try {
      await patchComponentRoles(selectedId, { remove: [role] });
      setSelectedRoles((prev) => (prev || []).filter((r) => r !== role));
      setSuccess("Role removed");
    } catch (e) {
      setError("Failed to remove role");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 0, md: 3 } }}>
      <Typography variant="h5" sx={{ mb: 2 }}>
        UI Permissions Management
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <TextField
            variant="outlined"
            placeholder="Search Component..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            size="small"
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          <Card sx={{ maxHeight: "620px", overflow: "auto" }}>
            <CardContent>
              <Box
                sx={{
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 1,
                }}
              >
                <Typography variant="subtitle1">Components</Typography>
                <IconButton onClick={load} disabled={loading}>
                  <RefreshIcon />
                </IconButton>
              </Box>
              <List>
                {filteredData.map((c) => (
                  <ListItem key={c?.id} button onClick={() => handleSelect(c)}>
                    <ListItemText
                      primary={c?.componentKey || c?.key || `#${c?.id}`}
                      secondary={
                        (c?.groupName || c?.group || "") +
                        (c?.active === false ? " • inactive" : "")
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>
                Selected Component
              </Typography>
              {!selectedId && (
                <Typography variant="body2">
                  Select a component to manage
                </Typography>
              )}
              {selectedId && (
                <>
                  <Box
                    sx={{
                      mb: 2,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <Chip
                      label={selectedActive ? "Active" : "Inactive"}
                      color={selectedActive ? "success" : "default"}
                    />
                    <Button
                      variant="outlined"
                      onClick={handleToggleActive}
                      disabled={loading}
                    >
                      {selectedActive ? "Deactivate" : "Activate"}
                    </Button>
                  </Box>

                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Allowed Roles
                  </Typography>
                  <Box
                    sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 1 }}
                  >
                    {(selectedRoles || []).map((r) => (
                      <Chip
                        key={r}
                        label={r}
                        onDelete={() => handleRemoveRole(r)}
                      />
                    ))}
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      gap: 1,
                      alignItems: "center",
                      mb: 1,
                    }}
                  >
                    <Select
                      size="small"
                      displayEmpty
                      value=""
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val) handleAddRole(val);
                      }}
                    >
                      <MenuItem value="">Add role…</MenuItem>
                      {allRoles
                        .filter((r) => !(selectedRoles || []).includes(r))
                        .map((r) => (
                          <MenuItem key={r} value={r}>
                            {r}
                          </MenuItem>
                        ))}
                    </Select>
                    <Button
                      variant="contained"
                      onClick={handleReplaceRoles}
                      disabled={loading}
                    >
                      Save Roles
                    </Button>
                  </Box>
                  {/* <Box
                    sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 1 }}
                  >
                    {(components || []).map((r) => (
                      <Chip
                        key={r}
                        label={r}
                        onDelete={() => handleRemoveRole(r)}
                      />
                    ))}
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      gap: 1,
                      alignItems: "center",
                      mb: 1,
                    }}
                  >
                    <Select
                      size="small"
                      displayEmpty
                      value=""
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val) handleAddRole(val);
                      }}
                    >
                      <MenuItem value="">Add role…</MenuItem>
                      {components
                        // .filter((r) => !(selectedRoles || []).includes(r))
                        .map((r) => (
                          <MenuItem key={r.id} value={r.id}>
                            {r.componentKey}
                          </MenuItem>
                        ))}
                    </Select>
                    <Button
                      variant="contained"
                      onClick={handleReplaceComponent}
                      disabled={loading}
                    >
                      Save Roles
                    </Button>
                  </Box> */}
                </>
              )}
            </CardContent>
          </Card>
          <Card sx={{ marginTop: "20px" }}>
            <CardContent>
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Create Component
                </Typography>
                <Grid container spacing={1}>
                  <Grid item xs={12}>
                    <TextField
                      size="small"
                      label="Key"
                      fullWidth
                      value={newKey}
                      onChange={(e) => setNewKey(e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      size="small"
                      label="Group"
                      fullWidth
                      value={newGroup}
                      onChange={(e) => setNewGroup(e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      size="small"
                      label="Description"
                      fullWidth
                      value={newDesc}
                      onChange={(e) => setNewDesc(e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Button
                      variant="contained"
                      disabled={!canCreate || loading}
                      onClick={handleCreate}
                    >
                      Create
                    </Button>
                  </Grid>
                </Grid>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Snackbar
        open={!!error}
        autoHideDuration={4000}
        onClose={() => setError("")}
        message={error}
      />
      <Snackbar
        open={!!success}
        autoHideDuration={2500}
        onClose={() => setSuccess("")}
        message={success}
      />
    </Box>
  );
}
